import { useCallback, useMemo } from 'react'
import { Platform } from 'react-native'

import { MotiView } from 'moti'
import Animated, { useAnimatedStyle } from 'react-native-reanimated'

import { useOnFocus, useOnHover } from '../hooks'
import { Icon } from '../icons'
import Pressable from '../pressable'
import { colors } from '../tailwind'

type CheckboxProps = {
  onChange: (checked: boolean) => void
  checked: boolean
  hitSlop?: number
  accesibilityLabel: string
  id?: string
  disabled?: boolean
  color?: string
}

export const Checkbox = ({
  checked,
  onChange,
  id,
  hitSlop = 14,
  accesibilityLabel,
  disabled,
  color,
}: CheckboxProps) => {
  const handleChange = useCallback(() => {
    onChange?.(!checked)
  }, [onChange, checked])

  const { onHoverIn, onHoverOut, hovered } = useOnHover()
  const { onFocus, onBlur, focused } = useOnFocus()

  const animatedStyle = useAnimatedStyle(() => {
    return {
      borderColor: hovered.value ? colors.grays[80] : color ? color : '#0A0A0A',
      // boxShadow:
      //   Platform.OS === 'web' && focused.value
      //     ? '0px 0px 0px 0px #E4E4E7'
      //     : undefined,
      disabled: disabled ? 0.4 : 1,
    }
  }, [focused, hovered, disabled])

  return (
    <Pressable
      onPress={handleChange}
      //@ts-ignore - web only prop
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      onFocus={onFocus}
      onBlur={onBlur}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={accesibilityLabel}
      disabled={disabled}
      hitSlop={hitSlop}
      //@ts-ignore - web only - checkbox toggle on spacebar press
      onKeyDown={Platform.select({
        web: (e: any) => {
          if (e.code === 'Space') handleChange()
        },
        default: undefined,
      })}
    >
      <Animated.View
        style={useMemo(
          () => [
            {
              height: 20,
              width: 20,
              borderRadius: 5,
              borderWidth: 2,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.grays[20],
            },
            animatedStyle,
          ],
          [animatedStyle]
        )}
      >
        <MotiView
          from={{ opacity: 0 }}
          animate={{ opacity: checked ? 1 : 0 }}
          transition={{ duration: 100, type: 'timing' }}
        >
          {checked ? (
            <Icon type="CheckBoxTickFilled" size={26} color={color} />
          ) : (
            <Icon type="CheckBox" size={26} />
          )}
        </MotiView>
      </Animated.View>
      {Platform.OS === 'web' && (
        <input
          disabled={disabled}
          type="checkbox"
          id={id}
          hidden
          onChange={handleChange}
          checked={checked}
        />
      )}
    </Pressable>
  )
}
